import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, X, Camera, CheckCircle2, RefreshCw, AlertCircle, Plus, GripVertical, RotateCw,
} from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";

const MODES = [
  { v: "auto",      icon: "✨" },
  { v: "bw",        icon: "◐" },
  { v: "grayscale", icon: "▦" },
  { v: "color",     icon: "🎨" },
  { v: "raw",       icon: "⊟" },
];

const LABELS_CA = {
  auto: "Auto",
  bw: "B/N",
  grayscale: "Grisos",
  color: "Color",
  raw: "Original",
};
const LABELS_ES = {
  auto: "Auto",
  bw: "B/N",
  grayscale: "Grises",
  color: "Color",
  raw: "Original",
};

const DESCRIPTIONS_CA = {
  auto: "Retall + endreça + grisos amb contrast (recomanat per OCR)",
  bw: "Negre i blanc clàssic CamScanner — fitxer més petit",
  grayscale: "Grisos amb contrast adaptat",
  color: "Manté els colors originals amb correcció lleugera",
  raw: "Sense cap processament — passa el fitxer tal qual",
};
const DESCRIPTIONS_ES = {
  auto: "Recorte + enderezado + grises con contraste (recomendado para OCR)",
  bw: "Blanco y negro clásico CamScanner — fichero más pequeño",
  grayscale: "Grises con contraste adaptado",
  color: "Mantiene los colores originales con corrección suave",
  raw: "Sin procesamiento — pasa el fichero tal cual",
};

const MAX_PAGES = 10;

/**
 * Scan preview dialog (CamScanner-style) with multi-page support.
 *
 * Props:
 *  - pages: File[]            list of pages (≥1). Last page is shown in the big preview.
 *  - onConfirm(mode)          user accepted → upload all pages (single or multipage)
 *  - onCancel()               user dismissed
 *  - onAddPage()              user wants to capture another page (parent triggers camera)
 *  - onRemovePage(index)      user removed a page from the strip
 *  - onReorderPages(from, to) user reordered pages by drag-and-drop
 *  - onRotatePage(index)      user rotated a page 90° CW
 *  - busy                     parent is uploading
 */
export default function ScanPreviewDialog({
  pages = [],
  onConfirm,
  onCancel,
  onAddPage,
  onRemovePage,
  onReorderPages,
  onRotatePage,
  busy = false,
}) {
  const { lang } = useI18n();
  const labels = lang === "es" ? LABELS_ES : LABELS_CA;
  const descs = lang === "es" ? DESCRIPTIONS_ES : DESCRIPTIONS_CA;

  const [mode, setMode] = useState("auto");
  const [scanning, setScanning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  // Drag-and-drop reorder state. dragIdxRef is the source of truth because
  // React state batches can leak across synchronous DnD events.
  const dragIdxRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  const lastPage = pages[pages.length - 1] || null;
  const pageCount = pages.length;
  const isMulti = pageCount > 1;
  const canAddMore = pageCount < MAX_PAGES;

  // Local-object URLs for the thumbnail strip
  const thumbUrls = useMemo(
    () => pages.map((f) => URL.createObjectURL(f)),
    [pages]
  );
  useEffect(
    () => () => thumbUrls.forEach((u) => URL.revokeObjectURL(u)),
    [thumbUrls]
  );

  // Re-scan whenever the LAST page or the mode changes
  useEffect(() => {
    let cancelled = false;
    if (!lastPage) return undefined;

    const run = async () => {
      setScanning(true);
      setError(null);
      try {
        const fd = new FormData();
        fd.append("file", lastPage);
        fd.append("mode", mode);
        const r = await api.post("/received-invoices/scan", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
          timeout: 30000,
        });
        if (cancelled) return;
        const blob = r.data;
        setPreviewUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return URL.createObjectURL(blob);
        });
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || e.message;
        setError(detail);
      } finally {
        if (!cancelled) setScanning(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [lastPage, mode]);

  // Cleanup processed-preview blob on unmount
  useEffect(() => () => {
    setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  }, []);

  const close = () => {
    if (busy || scanning) return;
    onCancel?.();
  };

  const totalSize = pages.reduce((s, f) => s + (f.size || 0), 0);

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-150"
         onClick={close}
         data-testid="scan-preview-overlay">
      <div className="w-full max-w-5xl max-h-[94vh] overflow-hidden bg-[#0B1220] border border-white/10 rounded-2xl shadow-2xl flex flex-col"
           onClick={(e) => e.stopPropagation()}
           data-testid="scan-preview-dialog">

        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-lg font-semibold text-white truncate flex items-center gap-2">
                {isMulti
                  ? (lang === "es" ? "Factura multi-página" : "Factura multi-pàgina")
                  : (lang === "es" ? "Escanear factura" : "Escanejar factura")}
                {isMulti && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-500/15 border border-blue-500/30 text-blue-200 font-medium"
                        data-testid="scan-page-counter">
                    {pageCount} {lang === "es" ? "páginas" : "pàgines"}
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-400 truncate">
                {descs[mode]}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy || scanning}
            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400 disabled:opacity-40 flex-shrink-0"
            data-testid="scan-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Page thumbnail strip — drag-and-drop reorder enabled */}
        <div className="px-5 py-3 border-b border-white/10 bg-black/30 flex-shrink-0">
          <div className="flex items-center gap-2 overflow-x-auto pb-1"
               data-testid="scan-page-strip"
               onDragOver={(e) => { if (dragIdxRef.current !== null) e.preventDefault(); }}>
            {pages.map((f, idx) => {
              const isCurrent = idx === pageCount - 1;
              const isDragging = dragIdx === idx;
              const isDropTarget = dropIdx === idx && dragIdx !== null && dragIdx !== idx;
              const canDrag = pageCount > 1 && !busy && !scanning;
              return (
                <div
                  key={`${f.name}-${idx}`}
                  draggable={canDrag}
                  onDragStart={(e) => {
                    if (!canDrag) return;
                    dragIdxRef.current = idx;
                    setDragIdx(idx);
                    e.dataTransfer.effectAllowed = "move";
                    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* Safari */ }
                  }}
                  onDragOver={(e) => {
                    if (dragIdxRef.current === null) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dropIdx !== idx) setDropIdx(idx);
                  }}
                  onDragLeave={() => {
                    setDropIdx((d) => (d === idx ? null : d));
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragIdxRef.current;
                    dragIdxRef.current = null;
                    setDragIdx(null); setDropIdx(null);
                    if (from === null || from === idx) return;
                    onReorderPages?.(from, idx);
                  }}
                  onDragEnd={() => {
                    dragIdxRef.current = null;
                    setDragIdx(null); setDropIdx(null);
                  }}
                  className={`relative group flex-shrink-0 w-14 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    isDropTarget
                      ? "border-emerald-400 scale-105 shadow-[0_0_14px_rgba(52,211,153,0.55)]"
                      : isCurrent
                      ? "border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.4)]"
                      : "border-white/10"
                  } ${isDragging ? "opacity-30 scale-95" : ""} ${canDrag ? "cursor-grab active:cursor-grabbing" : ""}`}
                  data-testid={`scan-page-thumb-${idx}`}
                  title={canDrag ? (lang === "es" ? "Arrastra para reordenar" : "Arrossega per reordenar") : undefined}
                >
                  <img
                    src={thumbUrls[idx]}
                    alt={`Page ${idx + 1}`}
                    className="w-full h-full object-cover pointer-events-none"
                    draggable={false}
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] text-white text-center py-0.5 font-mono pointer-events-none">
                    {idx + 1}
                  </div>
                  {canDrag && (
                    <div className="absolute top-0.5 left-0.5 bg-black/60 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <GripVertical size={10} className="text-white/70" />
                    </div>
                  )}
                  {pageCount > 1 && !busy && !scanning && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onRemovePage?.(idx); }}
                      className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 hover:bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title={lang === "es" ? "Eliminar página" : "Elimina pàgina"}
                      data-testid={`scan-page-remove-${idx}`}
                    >
                      <X size={9} />
                    </button>
                  )}
                  {!busy && !scanning && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onRotatePage?.(idx); }}
                      className={`absolute ${pageCount > 1 ? "top-5" : "top-0.5"} right-0.5 w-4 h-4 rounded-full bg-black/70 hover:bg-blue-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}
                      title={lang === "es" ? "Rotar 90°" : "Gira 90°"}
                      data-testid={`scan-page-rotate-${idx}`}
                    >
                      <RotateCw size={9} />
                    </button>
                  )}
                </div>
              );
            })}
            {canAddMore && (
              <button
                type="button"
                onClick={onAddPage}
                disabled={busy || scanning}
                className="flex-shrink-0 w-14 h-20 rounded-lg border-2 border-dashed border-blue-500/40 bg-blue-500/5 hover:bg-blue-500/10 hover:border-blue-500/60 transition-all flex flex-col items-center justify-center text-blue-300 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="scan-add-page-btn"
                title={lang === "es" ? "Añadir página" : "Afegeix pàgina"}
              >
                <Plus size={20} strokeWidth={2.5} />
                <span className="text-[9px] mt-0.5 font-medium leading-none">
                  {lang === "es" ? "Página" : "Pàgina"}
                </span>
              </button>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 text-[10px]">
            {pageCount > 1 ? (
              <span className="text-slate-500 flex items-center gap-1">
                <GripVertical size={10} />
                {lang === "es"
                  ? "Arrastra las miniaturas para reordenar"
                  : "Arrossega les miniatures per reordenar"}
              </span>
            ) : <span />}
            {!canAddMore && (
              <span className="text-amber-300 flex items-center gap-1">
                <AlertCircle size={10} />
                {lang === "es"
                  ? `Máximo ${MAX_PAGES} páginas por factura`
                  : `Màxim ${MAX_PAGES} pàgines per factura`}
              </span>
            )}
          </div>
        </div>

        {/* Preview area */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs">
              <AlertCircle size={14} />
              <span className="truncate">{String(error)}</span>
            </div>
          ) : null}

          {isMulti && (
            <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-mono text-[10px]">
                {lang === "es" ? `Página ${pageCount}` : `Pàgina ${pageCount}`}
              </span>
              {lang === "es"
                ? "— previsualización de la última página capturada"
                : "— previsualització de l'última pàgina capturada"}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Side label={lang === "es" ? "Original" : "Original"}
                  imgUrl={thumbUrls[thumbUrls.length - 1]}
                  loading={false}
                  testid="scan-original-img" />
            <Side label={lang === "es" ? "Procesada" : "Processada"}
                  imgUrl={previewUrl}
                  loading={scanning}
                  testid="scan-processed-img" />
          </div>
        </div>

        {/* Mode selector + actions */}
        <div className="px-5 py-4 border-t border-white/10 flex-shrink-0 space-y-3 bg-black/20">
          <div className="flex flex-wrap items-center gap-1.5">
            {MODES.map((m) => {
              const active = mode === m.v;
              return (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setMode(m.v)}
                  disabled={busy || scanning}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 ${
                    active
                      ? "bg-blue-600 border-blue-500 text-white shadow-[0_0_18px_rgba(59,130,246,0.35)]"
                      : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                  data-testid={`scan-mode-${m.v}`}
                >
                  <span className="text-base leading-none">{m.icon}</span>
                  <span>{labels[m.v]}</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-500 truncate">
              {pageCount} {pageCount === 1
                ? (lang === "es" ? "página" : "pàgina")
                : (lang === "es" ? "páginas" : "pàgines")}
              {" · "}
              {(totalSize / 1024 / 1024).toFixed(2)} MB
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="ghost"
                onClick={close}
                disabled={busy || scanning}
                className="text-slate-400 hover:text-white"
                data-testid="scan-cancel-btn"
              >
                {lang === "es" ? "Cancelar" : "Cancel·lar"}
              </Button>
              {canAddMore && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onAddPage}
                  disabled={busy || scanning}
                  className="border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20"
                  data-testid="scan-add-page-cta"
                >
                  <Plus size={14} className="mr-1.5" />
                  {lang === "es" ? "Añadir página" : "Afegeix pàgina"}
                </Button>
              )}
              <Button
                type="button"
                onClick={() => onConfirm(mode)}
                disabled={busy || scanning || !!error || pageCount === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="scan-confirm-btn"
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    {lang === "es" ? "Procesando..." : "Processant..."}
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={14} className="mr-2" />
                    {isMulti
                      ? (lang === "es"
                          ? `Procesar ${pageCount} páginas`
                          : `Processa ${pageCount} pàgines`)
                      : (lang === "es" ? "Procesar" : "Processa")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Side({ label, imgUrl, loading, testid }) {
  return (
    <div className="rounded-xl bg-black border border-white/10 overflow-hidden">
      <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-400 border-b border-white/5 flex items-center justify-between">
        <span>{label}</span>
        {loading && <RefreshCw size={11} className="animate-spin text-blue-400" />}
      </div>
      <div className="relative aspect-[3/4] flex items-center justify-center">
        {loading && !imgUrl && (
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
        )}
        {imgUrl && (
          <img
            src={imgUrl}
            alt={label}
            className={`max-w-full max-h-full object-contain transition-opacity ${loading ? "opacity-40" : "opacity-100"}`}
            data-testid={testid}
          />
        )}
      </div>
    </div>
  );
}
